import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Users, Wifi, ParkingCircle, Accessibility } from 'lucide-react';
import { useVenue } from '@/hooks/useApi';

const FACILITY_ICONS: Record<string, typeof Wifi> = {
  wifi: Wifi,
  parking: ParkingCircle,
  accessibility: Accessibility,
};

export default function VenueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { data: venue, isLoading, error } = useVenue(id || '');

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 animate-pulse space-y-4">
        <div className="h-6 bg-bg-secondary rounded w-1/4" />
        <div className="h-10 bg-bg-secondary rounded w-3/4" />
        <div className="h-60 bg-bg-secondary rounded" />
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-text-secondary mb-4">{t('common.error')}</p>
        <Link to="/venues" className="btn-primary">{t('common.backToList')}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <Link
        to="/venues"
        className="inline-flex items-center gap-1 text-primary text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <ArrowLeft className="w-4 h-4" /> {t('common.backToList')}
      </Link>

      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        {i18n.language === 'en' ? venue.nameEn : venue.name}
      </h1>

      {venue.imageUrl && (
        <div className="w-full rounded-lg overflow-hidden mb-8">
          <img src={venue.imageUrl} alt={venue.name} className="w-full h-auto max-h-96 object-cover" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5 text-primary" />
            <span className="font-bold">{t('venues.address')}</span>
          </div>
          <p className="text-text-secondary">{venue.address}</p>
        </div>
        <div className="card">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-primary" />
            <span className="font-bold">{t('venues.capacity')}</span>
          </div>
          <p className="text-text-secondary">{venue.capacity.toLocaleString()}</p>
        </div>
      </div>

      <div className="card mb-8">
        <p className="text-text-secondary leading-relaxed">{venue.description}</p>
      </div>

      {Array.isArray(venue.competitions) && venue.competitions.length > 0 && (
        <div className="card mb-8">
          <h2 className="font-bold text-lg mb-4">{t('venues.competitionsHere')}</h2>
          <div className="flex flex-wrap gap-2">
            {venue.competitions.map((comp) => (
              <span key={comp} className="badge">{comp}</span>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(venue.facilities) && venue.facilities.length > 0 && (
        <div className="card mb-8">
          <h2 className="font-bold text-lg mb-4">{t('venues.facilities')}</h2>
          <div className="flex flex-wrap gap-3">
            {venue.facilities.map((facility) => {
              const Icon = FACILITY_ICONS[facility.toLowerCase()];
              return (
                <div key={facility} className="flex items-center gap-2 px-3 py-2 bg-bg-secondary rounded-theme text-sm">
                  {Icon && <Icon className="w-4 h-4 text-primary" />}
                  {facility}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {venue.mapUrl && (
        <div className="card">
          <h2 className="font-bold text-lg mb-4">{t('venues.map')}</h2>
          <div className="w-full h-80 bg-bg-secondary rounded-lg overflow-hidden">
            <iframe
              src={venue.mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title={`${venue.name} map`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
